
import DeleteBackupServerDialog from '@/components/dialogs/backups/DeleteBackupServerDialog.vue';
import EditBackupServerDialog from '@/components/dialogs/backups/EditBackupServerDialog.vue';
import { BackupServerDto, BackupServerId, BackupServerRepositoryInterface } from '@/services/Backup/interfaces/index';
import { AxiosError } from 'axios';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  ListPaginationOptions,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    EditBackupServerDialog,
    DeleteBackupServerDialog,
    PageHeader,
  },
  inject: [
    'backupServerRepository',
  ],
})
export default class AllBackupServers extends Vue {
  public loading: boolean = false;

  public backupServerRepository: BackupServerRepositoryInterface;

  public backupServers: BackupServerDto[] = [];

  public options: ListPaginationOptions = defaultListOptions();
  public totalItems: number = 0;
  public LIST_FOOTER_PROPS = LIST_FOOTER_PROPS;

  public searchString: string = '';

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Name', value: 'name' },
    { text: 'Actions', value: 'actions' },
  ];

  public deleteDialog: string = '';
  public deleteDialogData: BackupServerDto | false = false;

  public editDialog: string = '';
  public editDialogData: BackupServerDto | null = null;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'backup_server_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async search(): Promise<void> {
    this.loading = true;
    try {
      await this.getBackupServers();
    } catch (err) {
      await this.onError(err);
    } finally {
      this.loading = false;
    }
  }

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.initialize();
  }

  public async initialize(): Promise<void> {
    await this.search();
  }

  public async getBackupServers(): Promise<void> {
    const result = await this.backupServerRepository.searchBackupServers({
      search: this.getSearchString(),
      page: this.options.page,
      pageSize: effectivePageSize(this.options.itemsPerPage),
    });

    this.totalItems = result.total;
    this.backupServers = result.data;
  }

  public getSearchString(): string | undefined {
    return this.searchString.trim() || undefined;
  }

  public async onPageChange(): Promise<void> {
    await this.syncRouteQuery();
    await this.search();
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    await this.syncRouteQuery();
    await this.search();
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'backup_server_list') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    await this.search();
  }

  public async createBackupServer(): Promise<void> {
    await this.$router.push({ name: 'backup_server_create' });
  }

  public async showEditDialogFor(id: BackupServerId): Promise<void> {
    try {
      const backupServer = await this.backupServerRepository.getBackupServer(id);
      if (!backupServer) {
        return;
      }

      this.editDialog = 'edit-backup-server-dialog';
      this.editDialogData = backupServer;
    } catch (err) {
      await this.onError(err);
    }
  }

  public async showDeleteDialogFor(id: BackupServerId): Promise<void> {
    try {
      const backupServer = await this.backupServerRepository.getBackupServer(id);
      if (!backupServer) {
        return;
      }

      this.deleteDialog = 'delete-backup-server-dialog';
      this.deleteDialogData = backupServer;
    } catch (err) {
      await this.onError(err);
    }
  }

  public async editBackupServer(backupServer: BackupServerDto): Promise<void> {
    try {
      await this.backupServerRepository.updateBackupServer(backupServer);

      this.$successDialog.show('Backup server updated successfully');

      await this.search();
    } catch (err) {
      await this.onError(err);
    }
  }

  public async deleteBackupServer(backupServer: BackupServerDto): Promise<void> {
    try {
      await this.backupServerRepository.deleteBackupServer(backupServer.id);

      this.$successDialog.show('Backup server deleted successfully');

      await this.search();
    } catch (err) {
      await this.onError(err);
    }
  }

  private async onError(err: unknown): Promise<void> {
    const asAxiosError = err as AxiosError;
    if (asAxiosError.isAxiosError) {
      this.$errorDialog.show(asAxiosError.response?.data?.message ?? 'An axios error occurred');
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    } else {
      this.$errorDialog.show('An unknown error occurred');
    }
  }
}
